import { Temporal } from '@js-temporal/polyfill';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import {
  previewShiftChange,
  type Shift,
  type ShiftChangePreview,
  updateShift,
} from '../api/crew.ts';
import { updateOffer } from '../api/offers.ts';
import { isTimeOffClash } from '../api/time-off.ts';
import type { RosterPage } from '../api/roster.ts';
import { updateTask } from '../api/tasks.ts';
import { Button } from '../components/button.tsx';
import {
  type Gesture,
  type GestureWindow,
  useTimelineGestures,
} from '../components/timeline-gestures.ts';
import type { Span } from '../roster-layout.ts';
import type { Drawn } from './roster-popovers.tsx';
import { barStyle } from './roster-bars.tsx';

// CB-REQ OPS-03 at v1.9105, CB-CONV §12 crew part 4C-2: what drawing, moving and resizing mean on
// the roster. The gestures are the shared hook's (components/timeline-gestures.ts); here a drawn
// window offers a shift, an offer or a task, filled into the existing form, and a moved or resized
// shift, task, offer or held day is saved by the change its popover already makes. A confirmed shift
// asks what CRW-14 would make of the move first, and beyond tolerance says so before anything is
// sent; a quiet move can be undone from the line that reports it.

const local = (instant: Temporal.Instant, zone: string) =>
  instant.toZonedDateTimeISO(zone).toPlainDateTime().toString({ smallestUnit: 'minute' });
const hhmm = (instant: Temporal.Instant, zone: string) => local(instant, zone).slice(11, 16);
const windowOf = (w: { startsAt: string; endsAt: string }): GestureWindow => ({
  startsAt: Temporal.Instant.from(w.startsAt),
  endsAt: Temporal.Instant.from(w.endsAt),
});

function length(w: GestureWindow): string {
  const minutes = (w.endsAt.epochMilliseconds - w.startsAt.epochMilliseconds) / 60_000;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** "+1h → 06:30 – 18:30", "ends 22:00 · +1h", "18:00 – 21:00 · 3h": the time chip's words. */
export function chipText(g: Gesture, zone: string): string {
  const times = `${hhmm(g.window.startsAt, zone)} – ${hhmm(g.window.endsAt, zone)}`;
  if (g.kind === 'draw') return `${times} · ${length(g.window)}`;
  /** "+1h30m", "−15m"; nothing before anything has moved. */
  const by = (ms: number) => {
    const minutes = Math.round(ms / 60_000);
    if (minutes === 0) return null;
    const a = Math.abs(minutes);
    const h = Math.floor(a / 60);
    return `${minutes < 0 ? '−' : '+'}${h ? `${h}h` : ''}${a % 60 ? `${a % 60}m` : ''}`;
  };
  if (g.kind === 'move') {
    const moved = by(g.window.startsAt.epochMilliseconds - g.from.startsAt.epochMilliseconds);
    return moved ? `${moved} → ${times}` : times;
  }
  const [word, instant, was] =
    g.edge === 'end'
      ? (['ends', g.window.endsAt, g.from.endsAt] as const)
      : (['starts', g.window.startsAt, g.from.startsAt] as const);
  const changed = by(instant.epochMilliseconds - was.epochMilliseconds);
  return `${word} ${hhmm(instant, zone)}${changed ? ` · ${changed}` : ''}`;
}

/** Beside a placed window: to its right, or to its left near the end of the span. */
function beside(style: CSSProperties): CSSProperties {
  const left = parseFloat(String(style.left));
  const right = left + parseFloat(String(style.width));
  return right > 75 ? { right: `calc(${100 - left}% + 6px)` } : { left: `calc(${right}% + 6px)` };
}

/** A confirmed shift moved beyond tolerance, waiting on Move and tell them or Put it back. */
interface Pending {
  readonly shift: Shift;
  readonly window: { start: string; end: string };
  readonly callAt: string | undefined;
  readonly preview: ShiftChangePreview;
}

/** The last quiet move, and how to put it back. */
interface LastMove {
  readonly label: string;
  readonly undo: () => Promise<unknown>;
}

/** Wide enough to drag: on a phone the timeline scrolls under the finger instead (OPS-03). */
function useWide(): boolean {
  const query = '(min-width: 640px)';
  const [wide, setWide] = useState(() =>
    typeof window === 'undefined' || !window.matchMedia ? true : window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const list = window.matchMedia(query);
    const on = () => setWide(list.matches);
    list.addEventListener('change', on);
    return () => list.removeEventListener('change', on);
  }, []);
  return wide;
}

export function useRosterDrag({
  data,
  span,
  zone,
  days,
  enabled,
  refresh,
}: {
  data: RosterPage | undefined;
  span: Span;
  zone: string;
  days: number;
  /** May manage shifts, not selecting, nothing open. */
  enabled: boolean;
  refresh: () => Promise<void>;
}) {
  const wide = useWide();
  const [drawn, setDrawn] = useState<{ track: string; window: GestureWindow } | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [last, setLast] = useState<LastMove | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // CRW-02 at v1.9106: a move onto someone's unavailability or leave, held until the office says.
  const [clash, setClash] = useState<{ message: string; anyway: () => Promise<void> } | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not save that move.');
    } finally {
      setBusy(false);
      await refresh();
    }
  }

  async function change(key: string, from: GestureWindow, to: GestureWindow, moved: boolean) {
    const [kind, id] = key.split(':') as ['shift' | 'task' | 'offer', string];
    setLast(null);
    setClash(null);
    if (kind === 'shift') {
      const shift = data?.shifts.find((s) => s.id === id);
      if (!shift) return;
      const z = shift.window.zone;
      const window = { start: local(to.startsAt, z), end: local(to.endsAt, z) };
      const old = { start: shift.window.localStart, end: shift.window.localEnd };
      // A call time of its own moves with a moved shift; one that was the start, the save moves.
      const ownCall = shift.localCallAt !== shift.window.localStart;
      const delta = to.startsAt.epochMilliseconds - from.startsAt.epochMilliseconds;
      const callAt =
        moved && ownCall
          ? local(Temporal.Instant.from(shift.callAt).add({ milliseconds: delta }), z)
          : undefined;
      const name = shift.crewMember?.name ?? 'Unfilled';
      await run(async () => {
        const confirmed = shift.state === 'accepted' && shift.confirmedAt;
        const preview = confirmed ? await previewShiftChange(id, window, callAt) : null;
        if (preview && (preview.outcome === 'reconfirm' || preview.outcome === 'reaccept')) {
          setPending({ shift, window, callAt, preview });
          return;
        }
        const outside = preview?.outsideTasks.length
          ? ` · outside it: ${preview.outsideTasks.map((t) => t.description).join(', ')}`
          : '';
        const save = async (through?: { reason?: string | null }) => {
          await updateShift(id, {
            window,
            ...(callAt ? { callAt } : {}),
            ...(through ? { throughTimeOff: through } : {}),
          });
          setLast({
            label: `Moved ${name}’s shift to ${window.start.slice(11)} – ${window.end.slice(11)}${outside}`,
            undo: () =>
              updateShift(id, {
                window: old,
                ...(callAt ? { callAt: shift.localCallAt } : {}),
              }),
          });
        };
        try {
          await save();
        } catch (error) {
          if (!isTimeOffClash(error)) throw error;
          setClash({
            message: error.message,
            anyway: () =>
              run(async () => {
                setClash(null);
                await save({});
              }),
          });
        }
      });
      return;
    }
    if (kind === 'task') {
      const task = data?.tasks.find((t) => t.id === id);
      if (!task) return;
      const z = task.window.zone;
      const window = { start: local(to.startsAt, z), end: local(to.endsAt, z) };
      const old = { start: task.window.localStart, end: task.window.localEnd };
      await run(async () => {
        await updateTask(id, { window });
        setLast({
          label: `Moved ${task.description} to ${window.start.slice(11)} – ${window.end.slice(11)}`,
          undo: () => updateTask(id, { window: old }),
        });
      });
      return;
    }
    const offer = data?.offers.find((o) => o.id === id);
    if (!offer) return;
    const z = offer.window.zone;
    const window = { start: local(to.startsAt, z), end: local(to.endsAt, z) };
    const old = { start: offer.window.localStart, end: offer.window.localEnd };
    await run(async () => {
      // OPS-11 at v1.9105: the shifts taken from it follow, as the offer's own save does.
      await updateOffer(id, { window });
      setLast({
        label: `Moved the offer to ${window.start.slice(11)} – ${window.end.slice(11)}`,
        undo: () => updateOffer(id, { window: old }),
      });
    });
  }

  const gestures = useTimelineGestures({
    from: span.from,
    to: span.to,
    zone,
    snapMinutes: days <= 3 ? 15 : 60,
    shortestMinutes: 30,
    enabled: enabled && wide && !pending && !busy && !clash,
    onDraw: (track, window) => {
      setLast(null);
      setDrawn({ track, window });
    },
    onMove: (id, from, to) => void change(id, from, to, true),
    onResize: (id, _edge, from, to) => void change(id, from, to, false),
  });

  /** A bar's place: where it is being dragged to, else where it is. */
  function styleFor(key: string, w: { startsAt: string; endsAt: string }): CSSProperties {
    const g = gestures.live;
    if (g && g.kind !== 'draw' && g.id === key) {
      return barStyle(span, {
        startsAt: g.window.startsAt.toString(),
        endsAt: g.window.endsAt.toString(),
      });
    }
    return barStyle(span, w);
  }

  return {
    gestures,
    drawn,
    clearDrawn: () => setDrawn(null),
    pending,
    last,
    problem,
    busy,
    styleFor,
    windowOf,
    /** Move and tell them: save the move CRW-14 judged beyond tolerance. */
    tell: () =>
      pending &&
      run(async () => {
        const save = (through?: { reason?: string | null }) =>
          updateShift(pending.shift.id, {
            window: pending.window,
            ...(pending.callAt ? { callAt: pending.callAt } : {}),
            ...(through ? { throughTimeOff: through } : {}),
          });
        setPending(null);
        try {
          await save();
        } catch (error) {
          if (!isTimeOffClash(error)) throw error;
          setClash({
            message: error.message,
            anyway: () =>
              run(async () => {
                setClash(null);
                await save({});
              }),
          });
        }
      }),
    /** Put it back: nothing was saved. */
    putBack: () => {
      setPending(null);
      setClash(null);
    },
    /** A move held on someone's time off: what it lands on, and saving it anyway. */
    clash,
    undo: () =>
      last &&
      run(async () => {
        await last.undo();
        setLast(null);
      }),
  };
}

export type RosterDrag = ReturnType<typeof useRosterDrag>;

/** What a drawn window can become; CRW-02 at v1.9106 adds a person's unavailability and leave. */
export type Makes = 'shift' | 'offer' | 'task' | 'unavailable' | 'leave';
const MAKES: Record<Makes, string> = {
  shift: 'Shift',
  offer: 'Offer',
  task: 'Task',
  unavailable: 'Unavailable',
  leave: 'Leave',
};

/**
 * What a track shows over its bars: the window being drawn or just drawn, with its chip and the
 * choice of what to make; or the chip of a bar on it being moved.
 */
export function TrackOverlay({
  drag,
  track,
  barKeys,
  span,
  zone,
  makes,
  onMake,
}: {
  drag: RosterDrag;
  track: string;
  /** The bars in this track, to show the chip of one being dragged. */
  barKeys: readonly string[];
  span: Span;
  zone: string;
  /** What a window drawn here can become. */
  makes: ReadonlyArray<Makes>;
  onMake: (what: Makes, drawn: Drawn) => void;
}) {
  const g = drag.gestures.live;
  const drawing = g?.kind === 'draw' && g.track === track ? g.window : null;
  const settled = !drawing && drag.drawn?.track === track ? drag.drawn.window : null;
  const window = drawing ?? settled;
  const dragged = g && g.kind !== 'draw' && barKeys.includes(g.id) ? g : null;
  let out: ReactNode = null;
  if (window) {
    const style = barStyle(span, {
      startsAt: window.startsAt.toString(),
      endsAt: window.endsAt.toString(),
    });
    out = (
      <>
        <div className="timeline-drawn" style={style} aria-hidden="true">
          <span className="timeline-chip">{chipText({ kind: 'draw', track, window }, zone)}</span>
        </div>
        {settled ? (
          <div
            className="timeline-chooser"
            // Beside the window, inside the row: below it, the timeline's scroll box would clip it.
            style={beside(style)}
            role="group"
            aria-label="Make a"
          >
            <span className="px-1 text-xs text-muted">Make a</span>
            {makes.map((what, i) => (
              <Button
                key={what}
                variant={i === 0 ? 'primary' : 'quiet'}
                className="small-btn"
                onClick={() => {
                  drag.clearDrawn();
                  onMake(what, {
                    start: local(settled.startsAt, zone),
                    end: local(settled.endsAt, zone),
                  });
                }}
              >
                {MAKES[what]}
              </Button>
            ))}
            <Button
              variant="quiet"
              className="small-btn"
              aria-label="Discard"
              onClick={() => drag.clearDrawn()}
            >
              <span aria-hidden="true">×</span>
            </Button>
          </div>
        ) : null}
      </>
    );
  } else if (dragged) {
    const style = barStyle(span, {
      startsAt: dragged.window.startsAt.toString(),
      endsAt: dragged.window.endsAt.toString(),
    });
    out = (
      <span className="timeline-chip" style={{ left: style.left }} aria-live="polite">
        {chipText(dragged, zone)}
      </span>
    );
  }
  return out;
}

/** A confirmed shift moved beyond tolerance: what it does to them, before anything is sent. */
export function MoveCallout({ drag }: { drag: RosterDrag }) {
  const p = drag.pending;
  if (!p) return null;
  const who = p.shift.crewMember?.name ?? 'They';
  const first = who.split(' ')[0];
  const what =
    p.preview.outcome === 'reaccept'
      ? `${first} gets the new times and must accept again; until then they’re not held.`
      : `${first} gets the new times and confirms again.`;
  const outside = p.preview.outsideTasks.map((t) => t.description);
  return (
    <div className="notice flex flex-wrap items-center gap-3 text-sm" role="alert">
      <span className="tag tag-accent">{who} confirmed this</span>
      <span className="min-w-0 flex-1">
        {what} {p.window.start.slice(11)} – {p.window.end.slice(11)}.
        {outside.length ? (
          <>
            {' '}
            Now outside it: <strong>{outside.join(', ')}</strong>.
          </>
        ) : null}
      </span>
      <Button className="small-btn" disabled={drag.busy} onClick={() => void drag.tell()}>
        Move and tell {first}
      </Button>
      <Button variant="quiet" className="small-btn" onClick={drag.putBack}>
        Put it back
      </Button>
    </div>
  );
}

/** The last quiet move, with Undo; and a move refused, in words. */
export function MoveLine({ drag }: { drag: RosterDrag }) {
  if (drag.clash) {
    return (
      <p className="notice flex flex-wrap items-center gap-2 text-sm" role="alert">
        <span className="min-w-0 flex-1">{drag.clash.message}</span>
        <Button
          className="small-btn"
          disabled={drag.busy}
          onClick={() => void drag.clash?.anyway()}
        >
          Move anyway
        </Button>
        <Button variant="quiet" className="small-btn" onClick={drag.putBack}>
          Put it back
        </Button>
      </p>
    );
  }
  if (drag.problem) {
    return (
      <p role="alert" className="alert">
        {drag.problem}
      </p>
    );
  }
  if (!drag.last) return null;
  return (
    <p className="flex flex-wrap items-center gap-2 text-sm" role="status">
      <span>{drag.last.label}</span>
      <Button
        variant="quiet"
        className="small-btn"
        disabled={drag.busy}
        onClick={() => void drag.undo()}
      >
        Undo
      </Button>
    </p>
  );
}
